/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * AllSetScreen — the final onboarding celebration. One moment of delight, then
 * into the app. The confetti row is non-essential: remove it and "Enter
 * MyAmbits" still completes onboarding.
 */

import { PartyPopper } from "lucide-react";
import OnboardingShell from "@/components/onboarding/OnboardingShell";

interface Props {
  onEnter: () => void;
  step: number;
  totalSteps: number;
}

export default function AllSetScreen({ onEnter, step, totalSteps }: Props) {
  return (
    <OnboardingShell
      step={step}
      totalSteps={totalSteps}
      ariaLabel="You're all set"
      ctaLabel="Enter MyAmbits"
      onCta={onEnter}
    >
      <div className="mx-auto flex max-w-md flex-col items-center pt-16 text-center">
        {/* Non-essential micro-celebration. */}
        <div
          aria-hidden="true"
          className="mb-2 flex items-center gap-2 text-2xl"
        >
          <span className="animate-bounce">🎉</span>
          <span className="animate-bounce [animation-delay:120ms]">✨</span>
          <span className="animate-bounce [animation-delay:240ms]">🎊</span>
        </div>
        <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-[#7C52F5]/12 text-[#7C52F5] shadow-[0_16px_40px_-18px_rgba(124,82,245,0.8)]">
          <PartyPopper size={38} aria-hidden="true" />
        </div>
        <h1 className="mt-6 text-3xl font-bold tracking-tight text-[#2A2342]">
          You're all set 🎉
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-[#6B5FA0]">
          Your circle's ready and your safety net is in place. Welcome to
          MyAmbits.
        </p>
      </div>
    </OnboardingShell>
  );
}
